import * as React from 'react';

type UploadResponse = {
    success: boolean;
    url?: string;
    error?: string;
    message?: string;
};

type UseFileUploadReturn = {
    uploadFile: (file: File, onUploadSuccess?: () => void) => Promise<UploadResponse>;
    isUploading: boolean;
    error: string | null | undefined;
    clearError: () => void;
};

export function useFileUpload(uploadUrl: string): UseFileUploadReturn {
    const [isUploading, setIsUploading] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string | null | undefined>(null);

    const clearError = React.useCallback(() => {
        setError(null);
    }, []);

    const uploadFile = React.useCallback(
        async (file: File, onUploadSuccess?: () => void): Promise<UploadResponse> => {
            setIsUploading(true);
            setError(null);

            try {
                const formData = new FormData();
                formData.append('file', file);

                const response = await fetch(uploadUrl, {
                    method: 'POST',
                    body: formData,
                });

                const result: UploadResponse = await response.json();

                if (!response.ok) {
                    const errorMessage =
                        result.error ||
                        result.message ||
                        `Upload failed with status ${response.status}`;
                    setError(errorMessage);

                    return {
                        success: false,
                        error: errorMessage,
                    };
                }

                if (!result.success) {
                    const errorMessage = result.error || result.message || 'Upload failed';
                    setError(errorMessage);

                    return {
                        success: false,
                        error: errorMessage,
                    };
                }

                if (onUploadSuccess) {
                    onUploadSuccess();
                }

                return result;
            } catch (err: any) {
                const errorMessage = err.message || 'Network error during upload';
                setError(errorMessage);

                return {
                    success: false,
                    error: errorMessage,
                };
            } finally {
                setIsUploading(false);
            }
        },
        [uploadUrl]
    );

    return {
        uploadFile,
        isUploading,
        error,
        clearError,
    };
}
